import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import TopUtilityBar from './TopUtilityBar';

export default function CommonHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [wisdomDropdown, setWisdomDropdown] = useState(false);
  const [servicesDropdown, setServicesDropdown] = useState(false);
  const location = useLocation();

  const isEnglish = location.pathname === '/en' || location.pathname.startsWith('/en/');
  const prefix = (href: string) => isEnglish ? (href === '/' ? '/en' : `/en${href}`) : href;
  const navItems = [
    { name: isEnglish ? 'Home' : 'ホーム', href: '/' },
    { name: isEnglish ? 'About' : '執事ベルについて', href: '/about' },
    { name: isEnglish ? 'Wisdom' : '執事の知恵', href: '/wisdom', dropdown: [
      { name: isEnglish ? 'All Wisdom' : '執事の知恵一覧', href: '/wisdom' },
      { name: isEnglish ? 'Hospitality' : 'おもてなし', href: '/wisdom/hospitality' },
      { name: isEnglish ? 'Etiquette' : 'エチケット', href: '/wisdom/etiquette' },
      { name: isEnglish ? 'Butler Skills' : '執事スキル', href: '/wisdom/butler-skills' },
      { name: isEnglish ? 'Elegance' : '日常の品格', href: '/wisdom/elegance' },
      { name: isEnglish ? 'All Articles' : 'コラム記事一覧', href: '/wisdom/articles' }
    ] },
    { name: isEnglish ? 'Services' : 'サービス', href: '/services', dropdown: [
      { name: isEnglish ? 'All Services' : 'サービス一覧', href: '/services' },
      { name: isEnglish ? 'Corporate Training' : '企業研修', href: '/services/corporate-training' },
      { name: isEnglish ? 'Private Butler' : 'プライベート執事', href: '/services/private-butler' },
      { name: isEnglish ? 'Manner Courses' : 'マナー講座', href: '/services/manner-course' },
      { name: isEnglish ? 'Consulting' : 'コンサルティング', href: '/services/consulting' }
    ] },
    { name: isEnglish ? 'Media' : 'メディア', href: '/media' },
    { name: isEnglish ? 'Contact' : 'お問い合わせ', href: '/contact' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDropdownEnter = (href: string) => {
    if (href === '/wisdom') setWisdomDropdown(true);
    if (href === '/services') setServicesDropdown(true);
  };

  const handleDropdownLeave = (href: string) => {
    // 少し遅延を追加してマウスの移動を許可
    setTimeout(() => {
      if (href === '/wisdom') setWisdomDropdown(false);
      if (href === '/services') setServicesDropdown(false);
    }, 150);
  };

  const handleDropdownMenuEnter = (href: string) => {
    // ドロップダウンメニュー内にマウスが入った時は表示を維持
    if (href === '/wisdom') setWisdomDropdown(true);
    if (href === '/services') setServicesDropdown(true);
  };

  const handleDropdownMenuLeave = (href: string) => {
    if (href === '/wisdom') setWisdomDropdown(false);
    if (href === '/services') setServicesDropdown(false);
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-black/95 backdrop-blur-md border-b border-amber-400/20 shadow-2xl' 
        : 'bg-black/90 backdrop-blur-md border-b border-amber-400/10'
    }`}>
      <TopUtilityBar />
      <div className="max-w-7xl mx-auto px-8">
        <div className="flex justify-between items-center h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Link to={isEnglish ? "/en" : "/"} className="relative group cursor-pointer" aria-label={isEnglish ? 'Butler Bell' : '執事ベル'}>
              <div className="absolute -inset-3 bg-amber-400/10 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative">
                <div 
                  className="text-4xl font-light tracking-wide bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent"
                  style={{ fontFamily: '"Noto Serif JP", serif' }}
                >
                  Butler Bell
                </div>
                
                <div className="absolute -bottom-2 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                
                <div 
                  className="text-xs text-amber-400/80 tracking-[0.3em] mt-1 font-light"
                  style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                >
                  PREMIUM SERVICE
                </div>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <div key={item.href} className="relative group">
                {item.dropdown ? (
                  <div
                    className="relative"
                    onMouseEnter={() => handleDropdownEnter(item.href)}
                    onMouseLeave={() => handleDropdownLeave(item.href)}
                  >
                    <Link
                      to={prefix(item.href)}
                      className={`group relative font-light transition-all duration-400 px-6 py-3 whitespace-nowrap cursor-pointer flex items-center ${
                        location.pathname.startsWith(item.href) 
                          ? 'text-amber-400' 
                          : 'text-gray-300 hover:text-amber-400'
                      }`}
                      style={{ 
                        fontFamily: '"Noto Sans JP", sans-serif',
                        letterSpacing: '0.05em'
                      }}
                    >
                      <span className="relative z-10 text-sm">{item.name}</span>
                      <i className="ri-arrow-down-s-line ml-1 text-xs"></i>
                      
                      <div className="absolute inset-0 bg-amber-400/5 rounded transform scale-0 group-hover:scale-100 transition-transform duration-400 origin-center"></div>
                      <div className="absolute bottom-0 left-1/2 w-0 h-px bg-gradient-to-r from-amber-400 to-amber-300 group-hover:w-4/5 transition-all duration-500" style={{ transform: 'translateX(-50%)' }}></div>
                    </Link>

                    {/* Dropdown Menu */}
                    {((item.href === '/wisdom' && wisdomDropdown) || (item.href === '/services' && servicesDropdown)) && (
                      <div 
                        className="absolute top-full left-0 w-64 bg-black/95 backdrop-blur-md border border-amber-400/20 rounded-lg shadow-2xl py-4"
                        onMouseEnter={() => handleDropdownMenuEnter(item.href)}
                        onMouseLeave={() => handleDropdownMenuLeave(item.href)}
                        style={{ marginTop: '0px' }}
                      >
                        {item.dropdown.map((dropItem) => (
                          <Link
                            key={dropItem.href + dropItem.name}
                            to={prefix(dropItem.href)}
                            className="block px-6 py-3 text-sm text-gray-300 hover:text-amber-400 hover:bg-amber-400/5 transition-all duration-300"
                            style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
                          >
                            {dropItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={prefix(item.href)}
                    className={`group relative font-light transition-all duration-400 px-6 py-3 whitespace-nowrap cursor-pointer ${
                      location.pathname === item.href 
                        ? 'text-amber-400' 
                        : 'text-gray-300 hover:text-amber-400'
                    }`}
                    style={{ 
                      fontFamily: '"Noto Sans JP", sans-serif',
                      letterSpacing: '0.05em'
                    }}
                  >
                    <span className="relative z-10 text-sm">{item.name}</span>
                    
                    <div className="absolute inset-0 bg-amber-400/5 rounded transform scale-0 group-hover:scale-100 transition-transform duration-400 origin-center"></div>
                    <div className="absolute bottom-0 left-1/2 w-0 h-px bg-gradient-to-r from-amber-400 to-amber-300 group-hover:w-4/5 transition-all duration-500" style={{ transform: 'translateX(-50%)' }}></div>
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative w-12 h-12 text-gray-300 hover:text-amber-400 cursor-pointer flex items-center justify-center border border-amber-400/30 rounded backdrop-blur-sm bg-white/5 transition-all duration-300 hover:border-amber-400/60"
            >
              <div className="relative w-6 h-6 flex flex-col justify-center items-center">
                <span className={`block w-6 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-0.5' : '-translate-y-1'}`}></span>
                <span className={`block w-6 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                <span className={`block w-6 h-0.5 bg-current transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-0.5' : 'translate-y-1'}`}></span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-8 border-t border-amber-400/20 bg-black/98 backdrop-blur-md max-h-[80vh] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <nav className="flex flex-col space-y-2">
              {navItems.map((item, index) => (
                <div key={item.href}>
                  <Link
                    to={prefix(item.href)}
                    className="group relative text-gray-300 hover:text-amber-400 font-light transition-all duration-400 py-4 px-6 border-l-2 border-transparent hover:border-amber-400/60 cursor-pointer block"
                    style={{ 
                      fontFamily: '"Noto Sans JP", sans-serif',
                      letterSpacing: '0.05em',
                      animationDelay: `${index * 100}ms`
                    }}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="relative z-10">{item.name}</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-400/5 to-transparent transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500"></div>
                    <div className="absolute left-2 top-1/2 w-1 h-1 bg-amber-400 rounded-full transform scale-0 group-hover:scale-100 transition-transform duration-300"></div>
                  </Link>
                  
                  {item.dropdown && (
                    <div className="ml-6 mt-2 space-y-1">
                      {item.dropdown.map((dropItem) => (
                        <Link
                          key={dropItem.href + dropItem.name}
                          to={prefix(dropItem.href)}
                          className="block py-2 px-5 sm:px-6 lg:px-8 text-sm text-gray-400 hover:text-amber-400 transition-colors duration-300"
                          onClick={() => setIsMenuOpen(false)}
                        >
                          {dropItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
